import * as z from "zod";

export const CONVERSATION_UNAVAILABLE = "Conversation unavailable";
export const CONVERSATION_REFERENCE_MAX_COUNT = 32;

/** Source identities are not grants. Authorize again before staging, sending or opening. */
export const ConversationReferenceInputSchema = z
  .object({
    botId: z.string().min(1).max(200),
    sourceSpaceId: z.string().min(1).max(200),
    threadId: z.string().min(1).max(200).optional(),
    messageId: z.string().min(1).max(200).optional(),
  })
  .strict();
export type ConversationReferenceInput = z.infer<typeof ConversationReferenceInputSchema>;

export const ConversationReferenceSchema = ConversationReferenceInputSchema.extend({
  threadId: z.string().min(1).max(200),
  title: z.string().max(1000),
});
export type ConversationReference = z.infer<typeof ConversationReferenceSchema>;

export const ConversationMessageBlockSchema = z
  .object({
    kind: z.literal("conversation"),
    botId: z.string().min(1).max(200),
    spaceId: z.string().min(1).max(200),
    threadId: z.string().min(1).max(200),
    messageId: z.string().min(1).max(200).optional(),
    title: z.string().max(1000),
  })
  .strict();

export function conversationMessageBlock(reference: ConversationReference) {
  return ConversationMessageBlockSchema.parse({
    kind: "conversation",
    botId: reference.botId,
    spaceId: reference.sourceSpaceId,
    threadId: reference.threadId,
    ...(reference.messageId ? { messageId: reference.messageId } : {}),
    title: reference.title,
  });
}

export function appendConversationReference(
  references: readonly ConversationReference[],
  reference: ConversationReference,
): readonly ConversationReference[] {
  const checked = ConversationReferenceSchema.parse(reference);
  if (
    references.some(
      (item) =>
        item.botId === checked.botId &&
        item.sourceSpaceId === checked.sourceSpaceId &&
        item.threadId === checked.threadId &&
        item.messageId === checked.messageId,
    )
  )
    return references;
  return [...references, checked];
}

/** Identity-only agent input; never source transcript or caller-provided text. */
export function conversationReferenceNote(reference: ConversationReference): string {
  return `Conversation reference: ${JSON.stringify({
    botId: reference.botId,
    sourceSpaceId: reference.sourceSpaceId,
    threadId: reference.threadId,
    ...(reference.messageId ? { messageId: reference.messageId } : {}),
  })}. Open through authorized conversation access; this reference does not grant access.`;
}
